/**
 * ObRejectsTriageTile — surfaces GET /api/admin/audit/ob-submit-rejects
 * inside the AdminTools page so Vanessa can pull the dedup'd list of
 * clients who hit a TOB-hash error during the Kevin Godfrey incident
 * window, and draft personal outreach for each one without leaving
 * the admin page.
 *
 * Read-only. Errors flow through window.errorMessage. No silent
 * fallbacks. The "Draft email" button opens the user's mail client
 * via mailto: with a pre-filled subject and body \u2014 nothing is sent
 * automatically.
 *
 * PR P (21 May 2026, Vanessa) — closes the last AUDIT 1 action item.
 */
import { useState } from "react";
import {
  AlertTriangle, Loader2, RefreshCw, Mail, ChevronDown, ChevronRight,
  Users, Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { errorMessage } from "../utils/errorMessage";

function _formatError(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

// ─── Outreach template builder ───────────────────────────────
// Mirrors ob_outreach_template.md (Space asset). Three variants:
//   A — single attempt, default
//   B — multiple attempts (>=3), apologetic opener
// "Already emailed PDF" variant is added by hand on a case-by-case
// basis (we cannot infer that from the audit row alone).
function _buildOutreachBody(contact) {
  const firstName = (contact.submitted_name || "").trim().split(/\s+/)[0] || "there";
  const matterRef = contact.matter_reference || "(matter reference not recorded)";
  const firstDate = (() => {
    // new Date(null) silently returns the epoch (1970-01-01) instead of
    // NaN, so we must guard the input value itself before constructing
    // the Date. Otherwise a missing first_attempt_at produces an email
    // saying "Between 1 January 1970 and earlier today" which is
    // embarrassing.
    if (!contact.first_attempt_at) return "earlier this month";
    try {
      const d = new Date(contact.first_attempt_at);
      if (Number.isNaN(d.getTime())) return "earlier this month";
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch (_) { return "earlier this month"; }
  })();
  const attempts = contact.attempt_count || 1;

  const opener = attempts >= 3
    ? `Our records show you tried to submit the form ${attempts} times. That is unacceptable from us and I am sorry for the wasted effort. The fault is now fixed and a single attempt should now go through cleanly.`
    : `I owe you a brief explanation and an apology. Between ${firstDate} and earlier today, our Client Onboarding form had a fault that prevented some completed submissions from going through. You may have seen an error message about an unauditable acceptance when you clicked Submit, or the form may simply have appeared to do nothing.`;

  return `Dear ${firstName},

${opener}

The fault is now fixed. If you would still like to instruct us on matter ${matterRef}, please use the same link we sent you and resubmit the form \u2014 your details are not retained from the failed attempt, so a fresh fill-in is needed.

If you have already emailed me your completed form as a PDF \u2014 one or two clients have \u2014 I have your details safely and will not ask you to redo anything. I will be in touch separately about the next step.

If you no longer wish to proceed for any reason, please reply to let me know and I will close the file with no further action on your part.

Thank you for bearing with us. Bonsai Law is small and we move quickly \u2014 that includes when we have to fix our own mistakes.

Kind regards,
Vanessa Challess
Director, Bonsai Law
vanessa@bonsai.law`;
}

function _buildOutreachSubject(_contact) {
  return "Your Bonsai Law onboarding form \u2014 please try again";
}

function _mailtoUrl(contact) {
  const to = encodeURIComponent(contact.submitted_email || "");
  const subject = encodeURIComponent(_buildOutreachSubject(contact));
  const body = encodeURIComponent(_buildOutreachBody(contact));
  return `mailto:${to}?subject=${subject}&body=${body}`;
}

function _formatTimestamp(iso) {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

// ─── Tile ────────────────────────────────────────────────────
export default function ObRejectsTriageTile({ apiBase, getHeaders }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [errorCodeFilter, setErrorCodeFilter] = useState("tob_hash_failed");
  const [showRaw, setShowRaw] = useState(false);

  const fetchRejects = async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${apiBase}/api/admin/audit/ob-submit-rejects`);
      if (errorCodeFilter) url.searchParams.set("error_code", errorCodeFilter);
      const resp = await fetch(url.toString(), {
        method: "GET",
        headers: getHeaders(),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        let detail = text;
        try {
          const j = JSON.parse(text);
          detail = j?.detail || text;
        } catch (_) { /* keep raw */ }
        throw new Error(`HTTP ${resp.status} \u2014 ${detail || resp.statusText}`);
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError(_formatError(e, "Failed to fetch OB submit rejects"));
    } finally {
      setLoading(false);
    }
  };

  const contacts = data?.tob_hash_failed_contacts || [];
  const totalRows = data?.total_rows_returned || 0;
  const uniqueClients = data?.tob_hash_failed_unique_clients || 0;
  const countsByError = data?.counts_by_error || {};

  return (
    <section className="glass-card p-6 rounded-md" data-testid="ob-rejects-triage-tile">
      <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> OB Submit Rejects {"\u2014"} Retroactive Triage
      </h2>
      <p className="text-xs text-txt-muted mb-4">
        Surfaces clients whose onboarding submission was rejected by the backend. Built for the
        21 May 2026 TOB-hash incident (PR L). One row per unique client (deduplicated by email
        or matter ref). Click <strong>Draft email</strong> to open a pre-filled message in your
        mail client {"\u2014"} nothing is sent until you press Send.
      </p>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <label className="text-xs text-txt-muted">Error code:</label>
        <select
          value={errorCodeFilter}
          onChange={(e) => setErrorCodeFilter(e.target.value)}
          className="h-8 px-2 rounded-md border border-navy-border bg-navy-card text-txt text-xs"
          data-testid="ob-rejects-error-filter"
        >
          <option value="tob_hash_failed">tob_hash_failed (Kevin Godfrey incident scope)</option>
          <option value="ob_submit_token_invalid">ob_submit_token_invalid</option>
          <option value="ob_submit_no_correlation">ob_submit_no_correlation</option>
          <option value="">(any)</option>
        </select>
        <Button
          onClick={fetchRejects}
          disabled={loading}
          size="sm"
          className="gap-1.5"
          data-testid="ob-rejects-fetch"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {loading ? "Loading\u2026" : "Fetch list"}
        </Button>
      </div>

      {error && (
        <div className="p-3 mb-3 rounded-md bg-red-900/20 border border-red-800 text-red-400 text-sm" data-testid="ob-rejects-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-md bg-navy-hover/60 border border-navy-border">
              <div className="text-[10px] uppercase tracking-wider text-txt-muted flex items-center gap-1">
                <Users className="w-3 h-3" /> Unique clients
              </div>
              <div className="text-xl font-semibold text-teal mt-1" data-testid="ob-rejects-unique-count">
                {uniqueClients}
              </div>
            </div>
            <div className="p-3 rounded-md bg-navy-hover/60 border border-navy-border">
              <div className="text-[10px] uppercase tracking-wider text-txt-muted flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Total reject rows
              </div>
              <div className="text-xl font-semibold text-teal mt-1" data-testid="ob-rejects-total-count">
                {totalRows}
              </div>
            </div>
            <div className="p-3 rounded-md bg-navy-hover/60 border border-navy-border">
              <div className="text-[10px] uppercase tracking-wider text-txt-muted">By error code</div>
              <div className="text-xs text-txt mt-1 space-y-0.5">
                {Object.keys(countsByError).length === 0 && <span className="text-txt-muted">\u2014</span>}
                {Object.entries(countsByError).map(([ec, n]) => (
                  <div key={ec} className="flex justify-between">
                    <span className="font-mono">{ec}</span>
                    <span className="font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {contacts.length === 0 ? (
            <div className="p-4 rounded-md bg-emerald-900/20 border border-emerald-800 text-emerald-300 text-sm" data-testid="ob-rejects-empty">
              No tob_hash_failed rejections found in the audit log for this filter. Nothing to triage.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs" data-testid="ob-rejects-table">
                <thead>
                  <tr className="text-left text-txt-muted border-b border-navy-border">
                    <th className="py-2 pr-3 font-semibold">Client</th>
                    <th className="py-2 pr-3 font-semibold">Email</th>
                    <th className="py-2 pr-3 font-semibold">Matter ref</th>
                    <th className="py-2 pr-3 font-semibold text-center">Attempts</th>
                    <th className="py-2 pr-3 font-semibold">First attempt</th>
                    <th className="py-2 pr-3 font-semibold">Last attempt</th>
                    <th className="py-2 pr-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr
                      key={`${c.submitted_email}-${c.matter_reference}-${i}`}
                      className="border-b border-navy-border/40 hover:bg-navy-hover/30"
                      data-testid={`ob-rejects-row-${i}`}
                    >
                      <td className="py-2 pr-3 text-txt">{c.submitted_name || <span className="text-txt-muted">\u2014</span>}</td>
                      <td className="py-2 pr-3 text-txt font-mono">{c.submitted_email || <span className="text-txt-muted">\u2014</span>}</td>
                      <td className="py-2 pr-3 text-txt font-mono">{c.matter_reference || <span className="text-txt-muted">\u2014</span>}</td>
                      <td className="py-2 pr-3 text-center">
                        <span className={
                          "inline-block px-2 py-0.5 rounded text-[10px] font-semibold " +
                          ((c.attempt_count || 0) >= 3
                            ? "bg-red-900/30 text-red-400"
                            : "bg-amber-900/30 text-amber-400")
                        }>
                          {c.attempt_count || 1}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-txt-muted">{_formatTimestamp(c.first_attempt_at)}</td>
                      <td className="py-2 pr-3 text-txt-muted">{_formatTimestamp(c.last_attempt_at)}</td>
                      <td className="py-2 pr-3">
                        {c.submitted_email ? (
                          <a
                            href={_mailtoUrl(c)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal/10 text-teal border border-teal/40 hover:bg-teal/20 text-[11px] font-semibold"
                            data-testid={`ob-rejects-draft-${i}`}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            <Mail className="w-3 h-3" />
                            Draft email
                          </a>
                        ) : (
                          <span className="text-txt-muted text-[11px]">no email</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowRaw(s => !s)}
            className="mt-4 inline-flex items-center gap-1 text-xs text-txt-muted hover:text-teal"
            data-testid="ob-rejects-raw-toggle"
          >
            {showRaw ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Raw JSON
          </button>
          {showRaw && (
            <pre className="mt-2 p-3 rounded-md bg-navy-card border border-navy-border text-[10px] text-txt-muted overflow-x-auto max-h-96" data-testid="ob-rejects-raw">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </>
      )}
    </section>
  );
}
